import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Send, ArrowLeft, ShieldAlert, Ban, Lock, ShieldOff } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState, PageLoading, Inbox } from "@/components/common";
import { BlockDialog } from "@/components/BlockDialog";

export function Messages() {
  const [convos, setConvos] = useState(null);
  useEffect(() => { api.get("/conversations").then(({ data }) => setConvos(data.items)); }, []);
  if (convos === null) return <PageLoading />;
  return (
    <div className="mx-auto max-w-2xl space-y-3">
      <h1 className="font-display text-2xl font-bold tracking-tight">Messages</h1>
      {convos.length === 0 ? <EmptyState icon={Inbox} title="No messages yet" subtitle="Message a seller or business to start a conversation." /> : (
        <div className="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
          {convos.map((c) => (
            <div key={c.id} data-testid={`convo-${c.id}`} className="flex items-center gap-3 p-3 hover:bg-secondary/50">
              <Link to={c.other_user ? `/u/${c.other_user.id}` : "#"} data-testid={`convo-avatar-${c.id}`} onClick={(e) => !c.other_user && e.preventDefault()}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-secondary font-semibold text-primary hover:ring-2 hover:ring-primary">
                {(c.other_user?.name || "U")[0]}
              </Link>
              <Link to={`/messages/${c.id}`} className="min-w-0 flex-1">
                <div className="flex items-center justify-between"><span className="font-medium">{c.other_user?.name || "User"}</span>{c.unread_count > 0 && <span className="rounded-full bg-accent px-1.5 text-[10px] font-bold text-white" data-testid={`convo-unread-${c.id}`}>{c.unread_count}</span>}</div>
                {c.context_label && <p className="text-[11px] text-primary">Re: {c.context_label}</p>}
                <p className="truncate text-sm text-muted-foreground">{c.last_message}</p>
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function Thread() {
  const { cid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [warning, setWarning] = useState(null);
  const [blockOpen, setBlockOpen] = useState(false);
  const endRef = useRef(null);

  const load = () => api.get(`/conversations/${cid}`).then(({ data }) => setData(data)).catch(() => nav("/messages"));
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [cid]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async (e) => {
    e.preventDefault(); if (!text.trim()) return;
    try {
      const { data: res } = await api.post(`/conversations/${cid}/messages`, { message: text });
      setText(""); setWarning(res.safety_warning); load();
    } catch (e) { toast.error(apiError(e)); load(); }
  };
  const unblock = async () => {
    if (!data?.other_user) return;
    try { await api.delete(`/blocks/${data.other_user.id}`); toast.success("User unblocked"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <PageLoading />;
  const bs = data.block_state || { can_message: true, blocked_by_me: false, blocked_by_them: false };
  const other = data.other_user;

  return (
    <div className="mx-auto flex h-[calc(100vh-11rem)] max-w-2xl flex-col">
      <div className="flex items-center gap-3 border-b border-border pb-3">
        <button onClick={() => nav("/messages")} data-testid="thread-back"><ArrowLeft className="h-5 w-5" /></button>
        <Link to={other ? `/u/${other.id}` : "#"} className="flex flex-1 items-center gap-2 hover:underline" data-testid="thread-profile-link">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary font-semibold text-primary">{(other?.name || "U")[0]}</div>
          <div><p className="font-medium">{other?.name}</p>{data.conversation.context_label && <p className="text-[11px] text-primary">Re: {data.conversation.context_label}</p>}</div>
        </Link>
        {bs.blocked_by_me ? (
          <Button variant="outline" size="sm" onClick={unblock} data-testid="thread-unblock"><ShieldOff className="h-4 w-4" />Unblock</Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={() => setBlockOpen(true)} data-testid="thread-block"><Ban className="h-4 w-4" />Block</Button>
        )}
      </div>
      <div className="flex-1 space-y-2 overflow-y-auto py-3">
        {warning && <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-200"><ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />{warning}</div>}
        {data.messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender_id === user.id ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${m.sender_id === user.id ? "bg-primary text-white" : "bg-secondary"}`}>{m.text}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      {bs.can_message ? (
        <form onSubmit={send} className="flex gap-2 border-t border-border pt-3">
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Type a message…" data-testid="thread-input" />
          <Button type="submit" data-testid="thread-send"><Send className="h-4 w-4" /></Button>
        </form>
      ) : (
        <div className="flex items-center gap-2 border-t border-border pt-3 text-sm text-muted-foreground" data-testid="thread-composer-blocked">
          <Lock className="h-4 w-4" />
          {bs.blocked_by_me ? "You blocked this user. Unblock them to send messages." : "You can no longer send messages in this conversation."}
        </div>
      )}
      {other && <BlockDialog open={blockOpen} onOpenChange={setBlockOpen} userId={other.id} userName={other.name} onBlocked={load} />}
    </div>
  );
}
